import { Controller } from '@hotwired/stimulus'

// FAQ accordion (E6 R4) — INDEPENDENT disclosure: each question is a native
// <button aria-expanded aria-controls> that toggles its own panel; opening one
// never closes another. Progressive enhancement: the ERB renders every answer
// expanded and visible, so no-JS visitors and crawlers read them. On connect
// this controller collapses every panel, then wires the toggle. Enter/Space and
// focus order come free from the <button>, so there is no key handling here and
// no roving tabindex to manage. All copy stays in the ERB — none lives in JS.
export default class extends Controller<HTMLElement> {
  static targets = ['trigger', 'panel']

  declare readonly triggerTargets: HTMLButtonElement[]
  declare readonly panelTargets: HTMLElement[]

  connect() {
    this.triggerTargets.forEach((trigger) => this.collapse(trigger))
  }

  toggle(event: Event) {
    const trigger = event.currentTarget as HTMLButtonElement
    if (trigger.getAttribute('aria-expanded') === 'true') {
      this.collapse(trigger)
    } else {
      this.expand(trigger)
    }
  }

  private expand(trigger: HTMLButtonElement) {
    trigger.setAttribute('aria-expanded', 'true')
    this.panelFor(trigger)?.removeAttribute('hidden')
  }

  private collapse(trigger: HTMLButtonElement) {
    trigger.setAttribute('aria-expanded', 'false')
    this.panelFor(trigger)?.setAttribute('hidden', '')
  }

  // Pairs a trigger with its panel via aria-controls, so DOM order never matters.
  private panelFor(trigger: HTMLButtonElement): HTMLElement | undefined {
    const id = trigger.getAttribute('aria-controls')
    return this.panelTargets.find((panel) => panel.id === id)
  }
}
